import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import LoadingAnimation from './components/LoadingAnimation';
import SiteNavigation from './components/SiteNavigation';
import Hero from './components/Hero';
import TextSection from './components/TextSection';
import Skills from './components/Skills';
import Game from './components/Game';
import EducationDetails from './components/EducationDetails';
import Experience from './components/Experience';
import Publications from './components/Publications';
import SectionDivider from './components/SectionDivider';
import SplineSection from './components/SplineSection';
import EndingSection from './components/EndingSection';
import BookReader from './components/BookReader';

function App() {
  const backgroundAudioRef = useRef<AudioContext | null>(null);
  const backgroundSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [musicReady, setMusicReady] = useState(false);
  const [showLoading, setShowLoading] = useState(true);
  const [showMainContent, setShowMainContent] = useState(false);
  const [autoplayAttempted, setAutoplayAttempted] = useState(false);
  const [showSkills, setShowSkills] = useState(false);
  const [showGame, setShowGame] = useState(false);
  const [showBookReader, setShowBookReader] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadBackgroundMusic = async () => {
      try {
        console.log('Starting to load background music...');
        // 创建AudioContext
        backgroundAudioRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        console.log('AudioContext created');
        
        // 加载音频文件
        console.log('Fetching /sounds/thisisme_[cut_1sec].mp3');
        const response = await fetch('/sounds/thisisme_[cut_1sec].mp3');
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await backgroundAudioRef.current.decodeAudioData(arrayBuffer);
        audioBufferRef.current = audioBuffer;
        
        if (mounted) {
          setMusicReady(true);
          console.log('Music loaded successfully, setting musicReady to true');
        }
      } catch (error) {
        console.warn('Failed to load background music:', error);
      }
    };

    loadBackgroundMusic();

    return () => {
      mounted = false;
      
      // 停止音频
      if (backgroundSourceRef.current) {
        try {
          backgroundSourceRef.current.stop();
          backgroundSourceRef.current.disconnect();
        } catch (error) {
          console.warn('Error stopping background music:', error);
        }
      }
      
      // 关闭音频上下文
      if (backgroundAudioRef.current) {
        try {
          backgroundAudioRef.current.close();
        } catch (error) {
          console.warn('Error closing background audio context:', error);
        }
      }
    };
  }, []);

  // 自动播放音乐
  useEffect(() => {
    const attemptAutoplay = async () => {
      if (musicReady && showMainContent && !autoplayAttempted && !soundEnabled) {
        setAutoplayAttempted(true);
        console.log('Attempting autoplay...');
        
        try {
          // 尝试自动播放
          if (backgroundAudioRef.current && audioBufferRef.current) {
            // 确保AudioContext处于运行状态
            if (backgroundAudioRef.current.state === 'suspended') {
              await backgroundAudioRef.current.resume();
            }

            // 创建音源
            const source = backgroundAudioRef.current.createBufferSource();
            const gainNode = backgroundAudioRef.current.createGain();
            
            source.buffer = audioBufferRef.current;
            source.loop = false;
            gainNode.gain.value = 0.15;
            
            source.connect(gainNode);
            gainNode.connect(backgroundAudioRef.current.destination);
            
            source.start(0);
            backgroundSourceRef.current = source;
            gainNodeRef.current = gainNode;
            setSoundEnabled(true);
            console.log('Autoplay successful');
          }
        } catch (error) {
          console.log('Autoplay failed (browser policy):', error);
          // 自动播放失败是正常的，用户需要手动点击
        }
      }
    };

    // 延迟一点时间再尝试自动播放，确保页面已经完全加载
    const timer = setTimeout(attemptAutoplay, 1000);
    return () => clearTimeout(timer);
  }, [musicReady, showMainContent, autoplayAttempted, soundEnabled]);

  // 监听用户第一次交互，尝试启动音频
  useEffect(() => {
    const handleFirstInteraction = async () => {
      if (musicReady && !soundEnabled && !autoplayAttempted) {
        console.log('User interacted, attempting to start music...');
        
        try {
          if (backgroundAudioRef.current && audioBufferRef.current) {
            if (backgroundAudioRef.current.state === 'suspended') {
              await backgroundAudioRef.current.resume();
            }

            const source = backgroundAudioRef.current.createBufferSource();
            const gainNode = backgroundAudioRef.current.createGain();
            
            source.buffer = audioBufferRef.current;
            source.loop = false;
            gainNode.gain.value = 0.15;
            
            source.connect(gainNode);
            gainNode.connect(backgroundAudioRef.current.destination);
            
            source.start(0);
            backgroundSourceRef.current = source;
            gainNodeRef.current = gainNode;
            setSoundEnabled(true);
            console.log('Music started after user interaction');
            
            // 移除事件监听器
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('touchstart', handleFirstInteraction);
          }
        } catch (error) {
          console.log('Failed to start music after interaction:', error);
        }
      }
    };

    // 只在自动播放失败且音乐未启用时添加事件监听器
    if (musicReady && showMainContent && !soundEnabled && autoplayAttempted) {
      document.addEventListener('click', handleFirstInteraction);
      document.addEventListener('touchstart', handleFirstInteraction);
    }

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, [musicReady, showMainContent, soundEnabled, autoplayAttempted]);


  // 处理加载动画完成
  const handleLoadingComplete = () => {
    setShowLoading(false);
    // 短暂延迟后显示主内容，增加过渡效果
    setTimeout(() => {
      setShowMainContent(true);
    }, 200);
  };

  // 监听打开Skills事件
  useEffect(() => {
    const handleOpenSkills = () => {
      setShowSkills(true);
    };

    window.addEventListener('openSkills', handleOpenSkills);
    return () => window.removeEventListener('openSkills', handleOpenSkills);
  }, []);

  // 监听打开Game事件
  useEffect(() => {
    const handleOpenGame = () => {
      setShowGame(true);
    };

    window.addEventListener('openGame', handleOpenGame);
    return () => window.removeEventListener('openGame', handleOpenGame);
  }, []);

  // 监听打开BookReader事件
  useEffect(() => {
    const handleOpenBookReader = () => {
      setShowBookReader(true);
    };

    window.addEventListener('openBookReader', handleOpenBookReader);
    return () => window.removeEventListener('openBookReader', handleOpenBookReader);
  }, []);

  return (
    <>
      {/* 加载动画 */}
      {showLoading && (
        <LoadingAnimation onComplete={handleLoadingComplete} />
      )}
      
      {showMainContent && <SiteNavigation />}

      {/* 主页面内容 */}
      <AnimatePresence>
        {showMainContent && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.6, 
              ease: [0.25, 0.46, 0.45, 0.94] 
            }}
            className="relative"
          >
            <div className="relative z-0 bg-[#2C2721]">
              <div className="sticky top-0 z-0 h-screen overflow-hidden">
                <Hero />
              </div>
              <TextSection />
            </div>

            <div className="relative z-30 bg-[#2C2721]">
              <EducationDetails />
              <Experience />
              <Publications />
              <SectionDivider />
              <SplineSection />
              <EndingSection />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Skills模态窗口 */}
      <Skills isOpen={showSkills} onClose={() => setShowSkills(false)} />
      
      {/* Game页面 */}
      <Game isOpen={showGame} onClose={() => setShowGame(false)} />
      
      {/* BookReader页面 */}
      <BookReader isOpen={showBookReader} onClose={() => setShowBookReader(false)} />
    </>
  );
}

export default App;
